import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Product, ProductSize } from '../types';
import { useShop } from '../context/ShopContext';
import { CURRENCY } from '../constants';
import SizeSelectionModal from './SizeSelectionModal';
import ImageModal from './ImageModal';
import { optimizeImage, generateSrcSet } from '../utils/imageOptimizer';

interface ProductCardProps {
  product: Product;
  priority?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, priority = false }) => {
  const { addToCart } = useShop();
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const handleAddToCartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.sizes && product.sizes.length > 0) {
      setShowSizeModal(true);
    } else {
      addToCart(product);
    }
  };

  const handleSizeSelect = (size: ProductSize) => {
    addToCart(product, size);
    setShowSizeModal(false);
  };

  return (
    <>
      <div className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-mauve-light/30 flex flex-col h-full">
        <div
          className="relative aspect-[3/4] overflow-hidden bg-mauve-base/50 cursor-zoom-in"
          onClick={() => setShowImageModal(true)}
          role="button"
          tabIndex={0}
          aria-label={`View ${product.name} details`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setShowImageModal(true);
            }
          }}
        >
          <img
            src={optimizeImage(product.image, 600)}
            srcSet={generateSrcSet(product.image)}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            alt={product.name}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />

          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="bg-white/80 p-1.5 rounded-full text-mauve-dark backdrop-blur-sm">
              <Icon icon="lucide:maximize-2" width="16" />
            </div>
          </div>

          {/* Quick Add Button - Always visible now for better mobile UX */}
          <button
            onClick={handleAddToCartClick}
            disabled={product.stock === 0}
            className={`absolute bottom-3 right-3 p-2.5 rounded-full shadow-lg flex items-center justify-center transition-all active:scale-95 ${product.stock > 0
              ? 'bg-mauve-dark text-white hover:bg-mauve-medium'
              : 'bg-gray-400 cursor-not-allowed'
              }`}
            aria-label="Add to cart"
          >
            <Icon icon="lucide:plus" width="18" />
          </button>

          {product.stock === 0 && (
            <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm uppercase tracking-wide">
              Sold Out
            </div>
          )}
        </div>

        <div className="p-3 md:p-5 flex flex-col flex-grow">
          <div className="flex justify-between items-start mb-1">
            <div>
              <p className="text-[10px] text-mauve-medium font-semibold uppercase tracking-wider mb-0.5">
                {product.category}
              </p>
              <h3 className="text-sm md:text-lg font-serif font-semibold text-mauve-text group-hover:text-mauve-dark transition-colors line-clamp-1">
                {product.name}
              </h3>
            </div>
          </div>

          {/* Description hidden on very small screens to save space */}
          <p className="hidden md:block text-sm text-gray-500 line-clamp-2 mb-4 h-10">
            {product.description}
          </p>

          <div className="flex items-center justify-between mt-auto pt-2">
            <span className="text-base md:text-xl font-medium text-mauve-dark">
              {CURRENCY}{product.price.toLocaleString()}
            </span>
            <span className={`text-[10px] md:text-xs ${product.stock < 5 ? 'text-orange-500' : 'text-gray-400'}`}>
              {product.stock > 0 ? `${product.stock} left` : 'Out of Stock'}
            </span>
          </div>
        </div>
      </div>

      {showSizeModal && (
        <SizeSelectionModal
          product={product}
          onClose={() => setShowSizeModal(false)}
          onSelectSize={handleSizeSelect}
        />
      )}

      {showImageModal && (
        <ImageModal
          imageSrc={product.image}
          altText={product.name}
          onClose={() => setShowImageModal(false)}
        />
      )}
    </>
  );
};

export default ProductCard;
